import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { FiEdit2, FiTrash2, FiPlus, FiSave, FiX, FiCalendar } from 'react-icons/fi';
import ConfirmModal from '../../../components/shared/ConfirmModal';

const api = axios.create({ baseURL: process.env.REACT_APP_BASE_URL });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export default function AdminHolidaysPage() {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, holiday: null, loading: false });
  
  // State for the row being edited
  const [editForm, setEditForm] = useState({ name: '', date: '', description: '' });
  
  // State for the new holiday row at the bottom
  const [newForm, setNewForm] = useState({ name: '', date: '', description: '' });

  const fetchHolidays = async () => {
    setLoading(true);
    try {
      const res = await api.get('/holidays');
      const raw = res.data;
      const list = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
      setHolidays(list);
    } catch (err) {
      toast.error('Failed to load holidays');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHolidays();
  }, []);

  const handleEditClick = (holiday) => {
    setEditingId(holiday.id);
    const dateStr = Array.isArray(holiday.date) 
      ? holiday.date.map(n => String(n).padStart(2, '0')).join('-')
      : holiday.date;
    setEditForm({ name: holiday.name, date: dateStr, description: holiday.description || '' });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ name: '', date: '', description: '' });
  };

  const saveEdit = async (id) => {
    if (!editForm.name || !editForm.date) return toast.error('Name and Date are required');
    try {
      await api.put(`/holidays/${id}`, editForm);
      toast.success(`Holiday "${editForm.name}" has been updated.`);
      setEditingId(null);
      fetchHolidays();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update holiday');
    }
  };

  const openDeleteModal = (holiday) => {
    setDeleteConfirm({ open: true, holiday, loading: false });
  };

  const confirmDeleteHoliday = async () => {
    const holiday = deleteConfirm.holiday;
    if (!holiday) return;
    setDeleteConfirm(prev => ({ ...prev, loading: true }));
    try {
      await api.delete(`/holidays/${holiday.id}`);
      toast.success(`Holiday "${holiday.name}" has been deleted.`);
      setDeleteConfirm({ open: false, holiday: null, loading: false });
      fetchHolidays();
    } catch (err) {
      toast.error('Failed to delete holiday');
      setDeleteConfirm(prev => ({ ...prev, loading: false }));
    }
  };

  const addHoliday = async () => {
    if (!newForm.name || !newForm.date) return toast.error('Name and Date are required');
    try {
      await api.post('/holidays', newForm);
      toast.success(`Holiday "${newForm.name}" has been created.`);
      setNewForm({ name: '', date: '', description: '' });
      fetchHolidays();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add holiday');
    }
  };

  const formatDateString = (dateObj) => {
    if (!dateObj) return '';
    if (Array.isArray(dateObj)) return dateObj.map(n => String(n).padStart(2, '0')).join('-');
    return dateObj.toString();
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <FiCalendar className="text-blue-600" /> Manage Holidays
          </h1>
          <p className="text-slate-500 text-sm mt-1">Add, edit, and remove official company holidays.</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] text-left text-sm text-slate-600">
            <thead className="bg-slate-50 border-b border-slate-200 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
              <tr>
                <th className="px-6 py-4">Holiday Name</th>
                <th className="px-6 py-4">Date</th>
                <th className="px-6 py-4">Description</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={4} className="px-6 py-8 text-center text-slate-400 font-medium">Loading holidays...</td></tr>
              ) : holidays.length === 0 ? (
                <tr><td colSpan={4} className="px-6 py-8 text-center text-slate-400 font-medium">No holidays found. Add one below.</td></tr>
              ) : (
                holidays.map((h) => (
                  <tr key={h.id} className="hover:bg-slate-50/50 transition-colors group">
                    {editingId === h.id ? (
                      <>
                        <td className="px-6 py-3">
                          <input type="text" value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} className="w-full px-3 py-1.5 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" placeholder="Name" />
                        </td>
                        <td className="px-6 py-3">
                          <input type="date" value={editForm.date} onChange={e => setEditForm({...editForm, date: e.target.value})} className="w-full px-3 py-1.5 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
                        </td>
                        <td className="px-6 py-3">
                          <input type="text" value={editForm.description} onChange={e => setEditForm({...editForm, description: e.target.value})} className="w-full px-3 py-1.5 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" placeholder="Description (Optional)" />
                        </td>
                        <td className="px-6 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button onClick={() => saveEdit(h.id)} className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded transition-colors" title="Save"><FiSave size={16} /></button>
                            <button onClick={cancelEdit} className="p-1.5 text-slate-400 hover:bg-slate-100 rounded transition-colors" title="Cancel"><FiX size={16} /></button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-6 py-4 font-bold text-slate-800">{h.name}</td>
                        <td className="px-6 py-4 font-medium text-slate-600">{formatDateString(h.date)}</td>
                        <td className="px-6 py-4 text-slate-500">{h.description || <span className="text-slate-300 italic">None</span>}</td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => handleEditClick(h)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors" title="Edit"><FiEdit2 size={16} /></button>
                            <button onClick={() => openDeleteModal(h)} className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors" title="Delete"><FiTrash2 size={16} /></button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))
              )}
              {/* Add New Row */}
              <tr className="bg-blue-50/30">
                <td className="px-6 py-4">
                  <input type="text" value={newForm.name} onChange={e => setNewForm({...newForm, name: e.target.value})} className="w-full px-3 py-1.5 text-sm border border-blue-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white" placeholder="New Holiday Name" />
                </td>
                <td className="px-6 py-4">
                  <input type="date" value={newForm.date} onChange={e => setNewForm({...newForm, date: e.target.value})} className="w-full px-3 py-1.5 text-sm border border-blue-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white" />
                </td>
                <td className="px-6 py-4">
                  <input type="text" value={newForm.description} onChange={e => setNewForm({...newForm, description: e.target.value})} className="w-full px-3 py-1.5 text-sm border border-blue-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white" placeholder="Description (Optional)" />
                </td>
                <td className="px-6 py-4 text-right">
                  <button onClick={addHoliday} disabled={!newForm.name || !newForm.date} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-bold text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                    <FiPlus size={14} /> Add
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmModal
        isOpen={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, holiday: null, loading: false })}
        onConfirm={confirmDeleteHoliday}
        loading={deleteConfirm.loading}
        title="Delete Holiday?"
        message={`Are you sure you want to delete "${deleteConfirm.holiday?.name || 'this holiday'}"?`}
        confirmText="Delete"
        variant="danger"
      />
    </div>
  );
}
